import {useState} from 'react';
import {Body2} from '@ui/atoms/typography';
import {CustomMarkdown} from '@ui/atoms/typography/markdown';
import {SectionCard} from '@ui/layouts/section-card';
import {Image} from '@ui/atoms/image';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {RotatingLines} from 'react-loader-spinner';
import {CHECKLISTS_ENABLED} from '../../checklist-availability';
import {getPulseScaleOption} from '../../shared/pulse-scale';

interface SubmissionHistoryItemProps {
  entry: {
    id: string;
    period: string;
    submittedOn: string;
    pulse?: {
      mood: string;
      feedback: string | null;
    };
    note: string;
    responses?: any;
  };
  emojiMap: Record<string, {emoji: any; label: string}>;
  isExpanded: boolean;
  submissionDetails: Record<string, any>;
  checkinsController: CompanyCheckinsController;
  onToggleExpand: (submissionId: string) => void;
}

export const SubmissionHistoryItem = ({
  entry,
  emojiMap,
  isExpanded,
  submissionDetails,
  checkinsController,
  onToggleExpand,
}: SubmissionHistoryItemProps) => {
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Use pulse mood from entry.pulse.mood if available, fallback to entry.moodKey
  const moodKey = entry.pulse?.mood || 'neutral';

  const formattedMoodKey =
    getPulseScaleOption(moodKey)?.key || moodKey.toLowerCase();

  const feedBackMood =
    entry.pulse?.feedback || entry.responses?.pulse?.feedback || '';

  const emojiDetails = emojiMap[formattedMoodKey];

  const detail = submissionDetails[entry.id];

  const handleToggleExpand = async () => {
    if (!isExpanded && !detail) {
      // Set loading state
      setIsLoadingDetails(true);

      // Fetch full checkin data with responses when expanding for the first time
      try {
        const fullCheckinData = await checkinsController.getCheckinByIdAndFetchResponses(
          entry.id,
        );
        if (fullCheckinData) {
          // Store the fetched data in submissionDetails
          submissionDetails[entry.id] = fullCheckinData;
        }
      } catch (error) {
        console.error('Failed to fetch checkin details:', error);
      } finally {
        // Remove loading state
        setIsLoadingDetails(false);
      }
    }
    onToggleExpand(entry.id);
  };

  const renderQuestions = () => {
    // Try to get questions from entry.responses first, then fallback to detail
    const questionsData =
      entry.responses?.questions || detail?.responses?.questions;

    if (!questionsData?.questions) {
      return <Body2 kind="textBody">No additional responses captured.</Body2>;
    }

    const answeredQuestions = questionsData.questions.filter(
      (question: any) => question?.response && question.response !== '',
    );

    if (answeredQuestions.length === 0) {
      return <Body2 kind="textBody">No additional responses captured.</Body2>;
    }

    return answeredQuestions.map((question: any, index: number) => (
      <div
        key={question?._id || question?.questionId || index}
        className="flex flex-col gap-1">
        <Body2 weight="semibold">{question?.question}</Body2>
        <CustomMarkdown
          text={`• ${question?.response}`}
          compact
          className="text-[14px] leading-[22px] text-textBody"
        />
      </div>
    ));
  };

  const renderPriorities = () => {
    // Try to get priorities from entry.responses first, then fallback to detail
    const prioritiesData =
      entry.responses?.priorities || detail?.responses?.priorities;

    if (
      !prioritiesData?.currentCheckin ||
      prioritiesData.currentCheckin.length === 0
    ) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        <Body2 weight="semibold">Priorities:</Body2>
        {prioritiesData.currentCheckin.map((priority: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={priority?.completed || false}
              readOnly
              className="w-4 h-4 rounded border border-gray-300"
            />
            <Body2 kind="textBody">{priority?.text || ''}</Body2>
          </div>
        ))}
      </div>
    );
  };

  return (
    <SectionCard
      key={entry.id}
      headerPadding="small"
      CustomHeaderTitle={
        <Body2 weight="semibold">Submitted {entry.submittedOn}</Body2>
      }
      contentPadding="large">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Body2 weight="semibold">How are things going for you?</Body2>

          {emojiDetails && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 ">
                <Image {...emojiDetails.emoji} />
              </div>

              <Body2 kind="textBody" weight="semibold">
                ({emojiDetails.label})
              </Body2>
            </div>
          )}

          {feedBackMood && (
            <Body2 kind="textBody" weight="semibold">
              • {feedBackMood}
            </Body2>
          )}
        </div>

        {isExpanded && (
          <div className="flex flex-col gap-4">
            {
              <>
                {CHECKLISTS_ENABLED && renderPriorities()}
                {renderQuestions()}
              </>
            }
          </div>
        )}

        <div className="mt-3">
          <button
            type="button"
            onClick={handleToggleExpand}
            className="text-[#585ADF] text-sm font-semibold hover:underline flex flex-row items-center gap-2">
            {isLoadingDetails && (
              <div className="">
                <RotatingLines
                  strokeColor="#585ADF"
                  strokeWidth="3"
                  animationDuration="0.75"
                  width="14"
                  visible={true}
                />
              </div>
            )}

            {isExpanded ? '- Collapse' : '+ Expand'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
};
